import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import endpoints from "../../config/endpoints";
import { Sidebar } from "../../layouts/Sidebar";
import { setLoading } from "../../services/reducers/homeSlice";
import restApi from "../../services/restApi";
import tools from "../../utils/tools";

const HomePageManage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const [form, setForm] = useState<any>({});
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState<string[]>([""]);
  const [banners, setBanners] = useState<any>({});
  const [selectedBrands, setSelectedBrands] = useState<any>([]);
  const [productGroups, setProductGroups] = useState<any>([
    { title: "home0", products: [] },
    { title: "home1", products: [] },
    { title: "home2", products: [] },
    { title: "home3", products: [] },
    { title: "home4", products: [] },
  ]);

  /* ── banner state helpers ─────────────────────────────── */
  const setBannerFile = (code: string, file: File) =>
    setBanners((prev) => {
      const data = { ...(prev[code] || {}) };
      data[code] = file;
      data.preview = URL.createObjectURL(file);
      data.mediaId = null;
      return { ...prev, [code]: data };
    });

  const setBannerField = (code: string, field: string, value: string) =>
    setBanners((prev) => {
      const data = { ...(prev[code] || {}) };
      data[field] = value;
      return { ...prev, [code]: data };
    });

  const removeBanner = (code: string) =>
    setBanners((prev) => {
      const cp = { ...prev };
      delete cp[code];
      return cp;
    });

  /* ── slider (slider0 = category circles) helpers ──────── */
  const sliderKey = (code: number, index: number) => `slider${code}-${index}`;

  const sliderItems = (code: number) =>
    Object.keys(banners).filter((k) => k.startsWith(`slider${code}-`));

  const addSliderBanner = (code: number) =>
    setBanners((prev) => ({
      ...prev,
      [sliderKey(code, sliderItems(code).length)]: {},
    }));

  const setSliderFile = (code: number, index: number, file: File) =>
    setBanners((prev) => {
      const k = sliderKey(code, index);
      const data = { ...(prev[k] || {}) };
      data[k] = file;
      data.preview = URL.createObjectURL(file);
      data.mediaId = null;
      return { ...prev, [k]: data };
    });

  const setSliderField = (
    code: number,
    index: number,
    field: string,
    value: string,
  ) =>
    setBanners((prev) => {
      const k = sliderKey(code, index);
      const data = { ...(prev[k] || {}) };
      data[field] = value;
      return { ...prev, [k]: data };
    });

  const removeSliderBanner = (code: number, removeIndex: number) =>
    setBanners((prev) => {
      const rest = Object.fromEntries(
        Object.entries(prev).filter(([k]) => !k.startsWith(`slider${code}-`)),
      );
      Object.entries(prev)
        .filter(([k]) => k.startsWith(`slider${code}-`))
        .filter((e, i) => i !== removeIndex)
        .forEach(([, value], i) => {
          rest[sliderKey(code, i)] = value;
        });
      return rest;
    });

  const removeProduct = (productGroupIndex: number, productIndex: number) =>
    setProductGroups((prev) => {
      const data = { ...prev[productGroupIndex] };
      data.products = data.products.filter((e, i) => i !== productIndex);
      return prev.map((e, index) => (index == productGroupIndex ? data : e));
    });

  const addProduct = (productGroupIndex: number) =>
    setProductGroups((prev) => {
      const data = { ...prev[productGroupIndex] };
      data.products = [...data.products, null];
      return prev.map((e, index) => (index == productGroupIndex ? data : e));
    });

  /* ── reusable banner slot (box + dimension hint + fields) ─ */
  const bannerSlot = (
    code: string,
    opts: {
      ratio: string;
      hint: string;
      className?: string;
      linkPlaceholder?: string;
      altPlaceholder?: string;
    },
  ) => {
    const banner = banners[code] || {};
    return (
      <div className={`hpb-banner ${opts.className || ""}`}>
        <div
          className="hpb-box"
          style={{
            aspectRatio: opts.ratio,
            backgroundImage: `url("${banner.preview || ""}")`,
          }}
        >
          <label htmlFor={code} className="hpb-plus">
            +
          </label>
          <input
            type="file"
            id={code}
            className="hidden"
            onChange={(input) =>
              input.target.files?.[0] &&
              setBannerFile(code, input.target.files[0])
            }
          />
          {banner.preview && (
            <span className="hpb-del" onClick={() => removeBanner(code)}>
              حذف
            </span>
          )}
          <span className="hpb-dim">{opts.hint}</span>
        </div>
        <div className="hpb-fields">
          <input
            type="text"
            className="hpb-input"
            placeholder={opts.altPlaceholder || "متن جایگزین"}
            value={banner.alt || ""}
            onChange={(input) => setBannerField(code, "alt", input.target.value)}
          />
          <input
            type="text"
            className="hpb-input"
            placeholder={opts.linkPlaceholder || "لینک"}
            value={banner.link || ""}
            onChange={(input) =>
              setBannerField(code, "link", input.target.value)
            }
          />
        </div>
      </div>
    );
  };

  /* ── category circles (slider0) ───────────────────────── */
  const categorySlider = (code: number) => (
    <div className="hpb-scroll">
      <span className="hpb-add" onClick={() => addSliderBanner(code)}>
        +
      </span>
      {sliderItems(code).map((_, index) => {
        const k = sliderKey(code, index);
        const banner = banners[k] || {};
        return (
          <div className="hpb-circle" key={k}>
            <div
              className="hpb-box hpb-box-round"
              style={{
                aspectRatio: "1 / 1",
                backgroundImage: `url("${banner.preview || ""}")`,
              }}
            >
              <label htmlFor={`${k}-file`} className="hpb-plus hpb-plus-sm">
                +
              </label>
              <input
                type="file"
                id={`${k}-file`}
                className="hidden"
                onChange={(input) =>
                  input.target.files?.[0] &&
                  setSliderFile(code, index, input.target.files[0])
                }
              />
              {banner.preview && (
                <span
                  className="hpb-del hpb-del-sm"
                  onClick={() => removeSliderBanner(code, index)}
                >
                  ×
                </span>
              )}
            </div>
            <input
              type="text"
              className="hpb-input"
              placeholder="عنوان"
              value={banner.alt || ""}
              onChange={(input) =>
                setSliderField(code, index, "alt", input.target.value)
              }
            />
            <input
              type="text"
              className="hpb-input"
              placeholder="لینک"
              value={banner.link || ""}
              onChange={(input) =>
                setSliderField(code, index, "link", input.target.value)
              }
            />
          </div>
        );
      })}
    </div>
  );

  /* ── product select card ──────────────────────────────── */
  const productCard = (groupIndex: number, slot: number) => (
    <div className="hpb-procard" key={`pg${groupIndex}-${slot}`}>
      <span className="hpb-procard-thumb">محصول</span>
      <Select
        options={tools.selectFormatter(products, "id", "title", "بدون والد")}
        value={{
          value: productGroups[groupIndex].products[slot],
          label: products.find(
            (e) => e.id == productGroups[groupIndex].products[slot],
          )?.title,
        }}
        className="select2 hpb-procard-select"
        classNamePrefix="hpbsel"
        placeholder="انتخاب محصول"
        menuPortalTarget={document.body}
        onChange={(selected: any) =>
          setProductGroups((prev) => {
            const data = { ...prev[groupIndex] };
            data.products[slot] = selected.value;
            return prev.map((e, index) => (index == groupIndex ? data : e));
          })
        }
      />
      {productGroups[groupIndex].products[slot] != null && (
        <span
          className="hpb-del-inline clickable"
          onClick={() => removeProduct(groupIndex, slot)}
        >
          حذف
        </span>
      )}
    </div>
  );

  /* ── product group row (mirrors the live carousels) ───── */
  const productGroupRow = (
    groupIndex: number,
    opts: {
      count?: number;
      title: string;
      variant?: "seeAll" | "side";
    },
  ) => {
    const { count = 4, title, variant = "seeAll" } = opts;
    const code = `productGroup${groupIndex}`;
    const liveTitle = banners[code]?.alt;

    const sideBanner = bannerSlot(code, {
      ratio: "2 / 3",
      hint:
        variant === "side"
          ? "بنر کناری • نسبت ۲:۳ • ۴۰۰×۶۰۰"
          : "مشاهده‌همه • نسبت ۲:۳ • ۲۱۶×۳۲۴",
      className: "hpb-pg-side",
      altPlaceholder: "عنوان دسته‌بندی",
    });

    // Show at least `count` slots, but let the admin add as many as they want.
    const slotCount = Math.max(productGroups[groupIndex].products.length, count);
    const cards = (
      <>
        {[...Array(slotCount)].map((_, slot) => productCard(groupIndex, slot))}
        <div
          className="hpb-procard hpb-procard-add clickable"
          onClick={() => addProduct(groupIndex)}
        >
          <span className="hpb-add">+</span>
          <span className="hpb-procard-addlabel">افزودن محصول</span>
        </div>
      </>
    );

    return (
      <section className="hpb-block">
        <div className="hpb-block-head">
          <span className="hpb-title">
            {title}
            {liveTitle ? ` — «${liveTitle}»` : ""}
          </span>
          <span className="hpb-note">
            عنوان نمایش‌داده‌شده در سایت = «متن جایگزینِ» بنرِ این ردیف
          </span>
        </div>
        <div className="hpb-pg">
          {variant === "side" ? (
            <>
              {cards}
              {sideBanner}
            </>
          ) : (
            <>
              {sideBanner}
              {cards}
            </>
          )}
        </div>
      </section>
    );
  };

  /* ── save ─────────────────────────────────────────────── */
  const send = async () => {
    dispatch(setLoading(true));

    const formData = new FormData();

    // Append a *consistent* set of fields per banner so the backend's
    // parallel arrays (code/mediaId/alt/link) never drift out of sync.
    // Empty slots (no image and no existing media) are skipped entirely.
    Object.entries(banners).forEach(([code, value]: any) => {
      if (!value) return;
      const hasFile = value[code] instanceof File;
      if (!hasFile && !value.mediaId && !value.preview) return; // empty slot

      if (hasFile) formData.append(code, value[code]); // fieldname === code
      formData.append("mediaId", value.mediaId == null ? "null" : value.mediaId);
      formData.append("alt", value.alt || "");
      formData.append("link", value.link || "");
      formData.append("code", code);
    });

    const homePage = await restApi(endpoints.page.basic + (paramId || "")).post(
      form,
    );

    if (!homePage?.data?.id) {
      dispatch(setLoading(false));
      Swal.fire({
        title: "ناموفق",
        text: homePage?.data || "ذخیره اطلاعات پایه صفحه ناموفق بود",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
      return;
    }

    const res = await Promise.all([
      restApi(endpoints.page.productGroups + homePage.data.id).put({
        productGroups: productGroups.map((group) => ({
          ...group,
          products: group.products.filter((e) => e !== "" && e != null),
        })),
      }),
      restApi(endpoints.page.brands + homePage.data.id).put({
        brands: selectedBrands.filter((e) => e !== "" && e != null),
      }),
      restApi(endpoints.page.banners + homePage.data.id).upload(formData),
      restApi(endpoints.page.posts + homePage.data.id).put({ posts: posts }),
    ]);

    // Each sub-save targets a different part of the page; report any that
    // failed instead of only looking at the product-groups response.
    const labels = ["گروه محصولات", "برندها", "بنرها", "مقالات"];
    const failed = res
      .map((r, i) => ({ ok: r?.code == 200, label: labels[i] }))
      .filter((e) => !e.ok);

    if (failed.length === 0) {
      Swal.fire({
        title: "موفق",
        text: `صفحه اصلی با موفقیت ${paramId ? "ویرایش" : "ساخته"} شد`,
        icon: "success",
        confirmButtonText: "متوجه شدم",
        didClose() {
          // navigate('/page');
        },
      });
    } else {
      Swal.fire({
        title: "ناموفق",
        text: `ذخیره این بخش‌ها ناموفق بود: ${failed.map((e) => e.label).join("، ")}`,
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    }

    dispatch(setLoading(false));
  };

  const fetchData = async () => {
    dispatch(setLoading(true));

    const res = await Promise.all([
      await restApi(endpoints.brand.index).get(),
      await restApi(endpoints.product.index).get(),
      paramId && (await restApi(endpoints.page.single + paramId).get()),
    ]);

    setBrands(res[0].data);
    setProducts(res[1].data);

    if (paramId) {
      setPosts(
        res[2].data?.posts?.map(
          (e) => process.env.REACT_APP_BLOG_URL + "/" + e.slug,
        ),
      );
      const newBanners = {};

      res[2].data.banners?.map((e) => {
        newBanners[e.code] = {
          preview: e.media.url,
          mediaId: e.media.id,
          data: undefined,
          alt: e.title,
          link: e.link,
        };
      });

      setBanners(newBanners);

      setSelectedBrands(res[2].data.brands?.map((e) => e.id));

      const fetchedGroups = res[2].data.productGroups || [];
      setProductGroups((prev) =>
        prev.map((group) => {
          const match = fetchedGroups.find((e) => e.title === group.title);
          return {
            ...group,
            products: match?.products?.map((p) => p.id) || [],
          };
        }),
      );

      setForm({
        title: "home",
        description: res[2].data.description,
      });

      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const hasSlider1 = sliderItems(1).length > 0;

  return (
    <main className="dashboardBody">
      <Sidebar />
      <div className="landingMain">
        <div className="addInfoHeader">
          <span className="dashboardHeader keepRight clickable" onClick={send}>
            {paramId ? "ویرایش" : "ثبت"} صفحه اصلی
          </span>
          <span>
            <h1 className="sideBarTitle">بازگشت به صفحه صفحه اصلی</h1>
            <h1 className="dashBoardTitle">
              {paramId
                ? `ویرایش صفحه اصلی ${form.title}`
                : "افزودن صفحه اصلی جدید"}
            </h1>
          </span>
          <i
            className="backAdd clickable"
            onClick={() => navigate("/homePage/")}
          ></i>
        </div>

        {/* Live homepage mirror */}
        <section className="hpb">
          <p className="hpb-legend">
            چیدمان زیر دقیقاً مطابق صفحه اصلی سایت است. روی هر کادر برای آپلود
            تصویر کلیک کنید؛ نسبت تصویر و ابعاد پیشنهادی روی هر بنر نوشته شده.
          </p>

          {/* 1 — Hero banners */}
          <div className="hpb-block">
            <div className="hpb-block-head">
              <span className="hpb-num">۱</span>
              <span className="hpb-title">بنرهای بالای صفحه (هیرو)</span>
            </div>
            <div className="hpb-hero">
              <div className="hpb-hero-main">
                {bannerSlot("top1", {
                  ratio: "16 / 10",
                  hint: "بنر بزرگ • نسبت ۱۶:۱۰ • ۱۲۰۰×۷۵۰",
                })}
              </div>
              <div className="hpb-hero-side">
                {bannerSlot("top2", {
                  ratio: "5 / 2",
                  hint: "نسبت ۵:۲ • ۸۰۰×۳۲۰",
                })}
                <div className="hpb-hero-row">
                  {bannerSlot("top3", {
                    ratio: "1 / 1",
                    hint: "مربع ۱:۱ • ۴۰۰×۴۰۰",
                  })}
                  {bannerSlot("top4", {
                    ratio: "1 / 1",
                    hint: "مربع ۱:۱ • ۴۰۰×۴۰۰",
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* 2 — Category circles (slider0) */}
          <div className="hpb-block">
            <div className="hpb-block-head">
              <span className="hpb-num">۲</span>
              <span className="hpb-title">دسته‌بندی‌های دایره‌ای</span>
              <span className="hpb-note">دایره • نسبت ۱:۱ • ۱۶۰×۱۶۰</span>
            </div>
            {categorySlider(0)}
          </div>

          {/* 3 — Product row (home0) */}
          {productGroupRow(0, { title: "ردیف محصولات ۱ (home0)" })}

          {/* 4 — Product row (home1) */}
          {productGroupRow(1, { title: "ردیف محصولات ۲ (home1)" })}

          {/* 5 — Full width banner */}
          <div className="hpb-block">
            <div className="hpb-block-head">
              <span className="hpb-num">۵</span>
              <span className="hpb-title">بنر تمام‌عرض (بالا)</span>
            </div>
            {bannerSlot("middleFullUp", {
              ratio: "4 / 1",
              hint: "تمام‌عرض • نسبت ۴:۱ • ۱۲۰۰×۳۰۰",
              className: "hpb-full",
            })}
          </div>

          {/* 6 — Brands */}
          <div className="hpb-block">
            <div className="hpb-block-head">
              <span className="hpb-num">۶</span>
              <span className="hpb-title">برندها</span>
              <span className="hpb-note">
                تصویر از پروفایل برند خوانده می‌شود • دایره ۱:۱ • ۱۲۸×۱۲۸
              </span>
            </div>
            <div className="hpb-scroll">
              <span
                className="hpb-add"
                onClick={() =>
                  setSelectedBrands((prev) => [...prev, brands[0]?.id])
                }
              >
                +
              </span>
              {selectedBrands.map((e, index) => (
                <div className="hpb-brand" key={"brand" + index}>
                  <span className="hpb-brand-thumb">برند</span>
                  <select
                    className="hpb-brand-select"
                    value={e}
                    onChange={(input) =>
                      setSelectedBrands((prev) =>
                        prev.map((b, i) =>
                          i == index ? input.target.value : b,
                        ),
                      )
                    }
                  >
                    {brands.map((brand) => (
                      <option value={brand.id} key={brand.id}>
                        {brand.title}
                      </option>
                    ))}
                  </select>
                  <span
                    className="hpb-del-inline clickable"
                    onClick={() =>
                      setSelectedBrands((prev) =>
                        prev.filter((b, i) => i !== index),
                      )
                    }
                  >
                    حذف
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 7 — Primary product row (home2) with side banner */}
          {productGroupRow(2, {
            count: 6,
            title: "ردیف محصولات ویژه (home2)",
            variant: "side",
          })}

          {/* 8 — Full width banner */}
          <div className="hpb-block">
            <div className="hpb-block-head">
              <span className="hpb-num">۸</span>
              <span className="hpb-title">بنر تمام‌عرض (پایین)</span>
            </div>
            {bannerSlot("middleFullDown", {
              ratio: "4 / 1",
              hint: "تمام‌عرض • نسبت ۴:۱ • ۱۲۰۰×۳۰۰",
              className: "hpb-full",
            })}
          </div>

          {/* 9 — Product row (home3) */}
          {productGroupRow(3, { title: "ردیف محصولات ۳ (home3)" })}

          {/* 10 — Two half banners (up) */}
          <div className="hpb-block">
            <div className="hpb-block-head">
              <span className="hpb-num">۱۰</span>
              <span className="hpb-title">دو بنر نیم‌عرض (بالا)</span>
            </div>
            <div className="hpb-grid2">
              {bannerSlot("middleRightUp", {
                ratio: "2 / 1",
                hint: "سمت راست • نسبت ۲:۱ • ۶۰۰×۳۰۰",
              })}
              {bannerSlot("middleLeftUp", {
                ratio: "2 / 1",
                hint: "سمت چپ • نسبت ۲:۱ • ۶۰۰×۳۰۰",
              })}
            </div>
          </div>

          {/* 11 — Product row (home4) */}
          {productGroupRow(4, { title: "ردیف محصولات ۴ (home4)" })}

          {/* 12 — Two half banners (down) */}
          <div className="hpb-block">
            <div className="hpb-block-head">
              <span className="hpb-num">۱۲</span>
              <span className="hpb-title">دو بنر نیم‌عرض (پایین)</span>
            </div>
            <div className="hpb-grid2">
              {bannerSlot("middleRightDown", {
                ratio: "2 / 1",
                hint: "سمت راست • نسبت ۲:۱ • ۶۰۰×۳۰۰",
              })}
              {bannerSlot("middleLeftDown", {
                ratio: "2 / 1",
                hint: "سمت چپ • نسبت ۲:۱ • ۶۰۰×۳۰۰",
              })}
            </div>
          </div>

          {/* 13 — Blog posts */}
          <div className="hpb-block">
            <div className="hpb-block-head">
              <span className="hpb-num">۱۳</span>
              <span className="hpb-title">مقالات بلاگ</span>
            </div>
            <div className="hpb-posts">
              {posts?.map((post, index) => (
                <span className="blogPosts" key={"postSpan" + index}>
                  <input
                    id={"post" + index}
                    className="editProductInput width100"
                    value={post}
                    onChange={(input) =>
                      setPosts((prev) => {
                        const cp = [...prev];
                        cp[index] = input.target.value;
                        return cp;
                      })
                    }
                  ></input>
                  <label htmlFor={"post" + index}>:URL</label>
                </span>
              ))}
              <button
                className="dashboardHeader"
                onClick={() => setPosts((prev) => [...(prev || []), ""])}
              >
                افزودن پست
              </button>
            </div>
          </div>

          {/* Admin-only: description + unused banners */}
          <div className="hpb-block hpb-block-extra">
            <div className="hpb-block-head">
              <span className="hpb-title">توضیحات صفحه (سئو)</span>
            </div>
            <textarea
              className="hpb-textarea"
              value={form.description || ""}
              onChange={(input) =>
                setForm((prev) => ({ ...prev, description: input.target.value }))
              }
            ></textarea>

            {hasSlider1 && (
              <div className="hpb-warn">
                <strong>اسلایدر دوم (slider1)</strong> — این بنرها در صفحه اصلی
                سایت نمایش داده نمی‌شوند (فرانت‌اند فقط slider0 را می‌خواند). در
                صورت نیاز آن‌ها را حذف کنید.
                <div className="hpb-scroll">
                  {sliderItems(1).map((_, index) => {
                    const k = sliderKey(1, index);
                    const banner = banners[k] || {};
                    return (
                      <div className="hpb-circle" key={k}>
                        <div
                          className="hpb-box hpb-box-round"
                          style={{
                            aspectRatio: "1 / 1",
                            backgroundImage: `url("${banner.preview || ""}")`,
                          }}
                        >
                          <span
                            className="hpb-del hpb-del-sm"
                            onClick={() => removeSliderBanner(1, index)}
                          >
                            ×
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default HomePageManage;
